import {useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Icon,
  IconButton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';
import {
  formatCurrencyFullPrecision,
  formatCurrencyHideTrailingZeroCents,
} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';

import ReviewSection from '@amblique/king-living/app/components/configuration-selector/review-section';
import SelectorList from '@amblique/king-living/app/components/configuration-selector/selector-list';
import {resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {SubtotalFooter} from '@amblique/king-living/app/components/complex-customize/option-step';

/**
 * Shared inner panel content: header label + scrollable list + sticky subtotal footer.
 * Used by both the mobile Drawer and the desktop inline panel.
 */
export const PanelContent = ({
  configProducts,
  isLoading,
  selectedId,
  onSelect,
  onDone,
  onViewDetails,
  showMobileImageHeader,
  selectedProduct,
  onExpandGallery,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const {currency: activeCurrency} = useCurrency();

  // Sort the selected item to the top once when products first load.
  // Using a ref so the order stays stable on subsequent re-renders (e.g. user selects another item).
  const sortedProductsRef = useRef(null);

  if (sortedProductsRef.current === null && configProducts.length > 0) {
    const idx = configProducts.findIndex((p) => (p.productId || p.id) === selectedId);

    if (idx > 0) {
      const sorted = [...configProducts];

      sorted.unshift(sorted.splice(idx, 1)[0]);
      sortedProductsRef.current = sorted;
    } else {
      sortedProductsRef.current = configProducts;
    }
  }

  const orderedProducts = sortedProductsRef.current ?? configProducts;

  const footerProduct = selectedProduct ? {
    imageUrl: resolveProductThumbnailUrl(selectedProduct),
    name: selectedProduct?.displayTitle ?? selectedProduct?.name ?? '',
  } : null;

  const selectedPrice = selectedProduct?.prices?.salePrice ?? selectedProduct?.price;
  const currencyCode = selectedProduct?.currency ?? activeCurrency;

  const priceText = selectedPrice != null ? (
    <FormattedMessage
      defaultMessage="Package price {price}"
      id="configuration_selector.subtotal.price"
      values={{
        price: (
          <Text
            as="b"
            aria-label={formatCurrencyFullPrecision(intl, selectedPrice, currencyCode)}
          >
            {formatCurrencyHideTrailingZeroCents(intl, selectedPrice, currencyCode)}
          </Text>
        ),
      }}
    />
  ) : null;

  return (
    <>
      {showMobileImageHeader && (
        <Box className="configuration-selector-panel__header-mobile" {...styles.mobileImageHeader}>
          <ReviewSection
            masterProduct={selectedProduct}
            showViewDetailsSection={false}
          />
          <IconButton
            {...styles.mobileExpandButton}
            aria-label="Expand gallery"
            icon={
              <Icon viewBox="0 0 24 24">
                <path
                  d="M3 3h7v2H5v5H3V3zm11 0h7v7h-2V5h-5V3zM3 14h2v5h5v2H3v-7zm16 5h-5v2h7v-7h-2v5z"
                  fill="currentColor"
                />
              </Icon>
            }
            onClick={onExpandGallery}
          />
        </Box>
      )}

      <Box className="configuration-selector-panel-content__body" {...styles.body}>
        <SelectorList
          isLoading={isLoading}
          products={orderedProducts}
          selectedId={selectedId}
          onSelect={onSelect}
          onViewDetails={onViewDetails}
        />
      </Box>

      {/* Sticky footer with subtotal and done button */}
      <SubtotalFooter
        emptyTitleContext="nestedSelection"
        footerProduct={footerProduct}
        priceText={priceText}
        styles={styles}
      >
        <Button
          {...styles.subtotalDoneButton}
          isDisabled={!selectedProduct}
          onClick={onDone}
        >
          <FormattedMessage defaultMessage="Done" id="configuration_selector.subtotal.done" />
        </Button>
      </SubtotalFooter>
    </>
  );
};

PanelContent.displayName = 'PanelContent';

PanelContent.propTypes = {
  configProducts: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  showMobileImageHeader: PropTypes.bool,
  selectedProduct: PropTypes.object,
  onExpandGallery: PropTypes.func,
};
